import type { Assumptions, EventItem, Scenario } from '../types';
import { uuid } from '../utils/uuid';

/**
 * FIRE-flavored scenario presets. Each template takes the user's current
 * assumptions so contribution deltas are relative to reality. The goal is
 * to give someone browsing Scenarios a running start — 4-5 branch scenarios
 * they can switch between in a click.
 */

export interface ScenarioTemplate {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  longBlurb: string;
  /** Event list, parameterized by current assumptions. */
  build: (a: Assumptions) => EventItem[];
}

const now = () => new Date().toISOString();

export const TEMPLATES: ScenarioTemplate[] = [
  {
    id: 'tpl-blank',
    name: 'Blank scenario',
    icon: '•',
    color: '#525b6f',
    description: 'Start from scratch — no events',
    longBlurb:
      'An empty plan. Layer events on top one at a time to explore a specific "what if".',
    build: () => [],
  },
  {
    id: 'tpl-sabbatical',
    name: 'Sabbatical',
    icon: '🏖️',
    color: '#0891b2',
    description: 'Take a year off in 3 years',
    longBlurb:
      'A 1-year no-income window 3 years from now. Contributions pause; HYSA covers living costs.',
    build: () => [
      mkJobGap(3, 'Sabbatical', 1),
    ],
  },
  {
    id: 'tpl-coast',
    name: 'Coast FIRE',
    icon: '🛟',
    color: '#8b5cf6',
    description: 'Stop saving in 5 years; keep working',
    longBlurb:
      'Invested enough that compounding alone reaches your nest-egg target. You still work for current expenses but stop all new contributions.',
    build: () => [
      mkContribChange(5, 'Stop all savings', {
        newRetirement: 0,
        newTaxable: 0,
        newHysa: 0,
      }),
    ],
  },
  {
    id: 'tpl-barista',
    name: 'Barista FIRE',
    icon: '☕',
    color: '#f59e0b',
    description: 'Drop to part-time in 3 years',
    longBlurb:
      'New part-time job at ~$60k/yr in 3 years. Retirement savings stop; part-time work covers current expenses (top up with savings if needed).',
    build: () => [
      mkIncomeChange(3, 'Part-time work', 60_000),
      mkContribChange(3, 'Stop accumulation', {
        newRetirement: 0,
        newTaxable: 0,
        newHysa: 0,
      }),
    ],
  },
  {
    id: 'tpl-lean',
    name: 'Lean FIRE',
    icon: '🍃',
    color: '#14b8a6',
    description: 'Cut spending, retire earlier',
    longBlurb:
      'Trim monthly spending by 30% and retire 3 years earlier than planned.',
    build: a => [
      {
        id: uuid(),
        kind: 'spending_change',
        yearOffset: 0,
        name: 'Lean spending',
        enabled: true,
        createdAt: now(),
        newMonthlySpend: Math.round(a.monthlyHouseholdSpend * 0.7),
      },
      {
        id: uuid(),
        kind: 'retire',
        yearOffset: 0,
        name: 'Retire 3 years earlier',
        enabled: true,
        createdAt: now(),
        retirementAge: Math.max(a.currentAge + 1, a.targetRetirementAge - 3),
      },
    ],
  },
  {
    id: 'tpl-retire-early',
    name: 'Retire 5 years earlier',
    icon: '🌴',
    color: '#0891b2',
    description: 'Pull retirement forward',
    longBlurb:
      'Shifts the retirement age 5 years earlier than your assumption. Same savings, sooner burn.',
    build: a => [
      {
        id: uuid(),
        kind: 'retire',
        yearOffset: 0,
        name: 'Retire earlier',
        enabled: true,
        createdAt: now(),
        retirementAge: Math.max(a.currentAge + 1, a.targetRetirementAge - 5),
      },
    ],
  },
  {
    id: 'tpl-retire-late',
    name: 'Work 5 more years',
    icon: '⏳',
    color: '#64748b',
    description: 'Push retirement back',
    longBlurb:
      'Delays retirement by 5 years. Same spending; 5 more years of compounding and 5 fewer years of drawdown.',
    build: a => [
      {
        id: uuid(),
        kind: 'retire',
        yearOffset: 0,
        name: 'Retire later',
        enabled: true,
        createdAt: now(),
        retirementAge: a.targetRetirementAge + 5,
      },
    ],
  },
  {
    id: 'tpl-upsize',
    name: 'Upsize the home',
    icon: '🏡',
    color: '#059669',
    description: 'Sell + buy a bigger place in 3 years',
    longBlurb:
      'Sell your current home (using §121) and buy a home 50% more expensive. New 30-yr mortgage.',
    build: a => [
      {
        id: uuid(),
        kind: 'home_buy',
        yearOffset: 3,
        name: 'Bigger house',
        enabled: true,
        createdAt: now(),
        purchasePrice: Math.round(a.homeCostBasis * 1.5 + 400_000),
        downPayment: 200_000,
        mortgagePrincipal: Math.round(a.homeCostBasis * 1.5 + 200_000),
        mortgageAnnualRate: 0.065,
        mortgageTermYears: 30,
        sellCurrentHome: true,
        newCostBasis: Math.round(a.homeCostBasis * 1.5 + 400_000),
      },
    ],
  },
  {
    id: 'tpl-downsize',
    name: 'Downsize home at retirement',
    icon: '🏠',
    color: '#14b8a6',
    description: 'Free up equity by moving smaller',
    longBlurb:
      'At retirement age, sell the home and move into something 40% cheaper. Net proceeds top up HYSA.',
    build: a => {
      const retireYearOffset = Math.max(1, a.targetRetirementAge - a.currentAge);
      return [
        {
          id: uuid(),
          kind: 'home_sell',
          yearOffset: retireYearOffset,
          name: 'Downsize',
          enabled: true,
          createdAt: now(),
          salePrice: Math.round(a.homeCostBasis * 1.3), // rough appreciation
          useSection121: true,
          proceedsBucket: 'hysa',
        },
      ];
    },
  },
  {
    id: 'tpl-inheritance',
    name: 'Inheritance',
    icon: '💰',
    color: '#f59e0b',
    description: 'One-time $500k windfall in 10 years',
    longBlurb:
      'Models a $500k tax-free inheritance 10 years out, deposited to taxable.',
    build: () => [
      {
        id: uuid(),
        kind: 'windfall',
        yearOffset: 10,
        name: 'Inheritance',
        enabled: true,
        createdAt: now(),
        amount: 500_000,
        toBucket: 'taxable',
        taxFree: true,
      },
    ],
  },
];

function mkJobGap(yearOffset: number, name: string, durationYears: number): EventItem {
  return {
    id: uuid(),
    kind: 'job_gap',
    yearOffset,
    name,
    enabled: true,
    durationYears,
    createdAt: now(),
  };
}

function mkIncomeChange(yearOffset: number, name: string, newAnnualSalary: number): EventItem {
  return {
    id: uuid(),
    kind: 'income_change',
    yearOffset,
    name,
    enabled: true,
    newAnnualSalary,
    createdAt: now(),
  };
}

function mkContribChange(
  yearOffset: number,
  name: string,
  values: { newRetirement?: number; newTaxable?: number; newHysa?: number },
): EventItem {
  return {
    id: uuid(),
    kind: 'contribution_change',
    yearOffset,
    name,
    enabled: true,
    ...values,
    createdAt: now(),
  };
}

/** Instantiate a template into a fresh Scenario (with new event IDs). */
export function scenarioFromTemplate(
  template: ScenarioTemplate,
  assumptions: Assumptions,
  order: number,
): Scenario {
  return {
    id: uuid(),
    name: template.name,
    description: template.description,
    icon: template.icon,
    color: template.color,
    order,
    archived: false,
    events: template.build(assumptions),
    createdAt: now(),
  };
}

/** Duplicate a scenario with fresh IDs everywhere. */
export function duplicateScenario(s: Scenario, order: number): Scenario {
  return {
    ...s,
    id: uuid(),
    name: `${s.name} (copy)`,
    order,
    events: s.events.map(e => ({ ...e, id: uuid() } as EventItem)),
    createdAt: now(),
  };
}
